'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MAP_CONFIGS, MapConfig } from '@/lib/mapConfigs';
import GameScene from './GameScene';
import ResultOverlay from './ResultOverlay';
import { CoreSettingsPanel, AdvancedSettingsPanel, TrainerSettings, SpawnOffset } from './SettingsPanel';
import { AdvancedHitbox, DEFAULT_ADVANCED_HITBOX } from './TargetSilhouette';
import { SKIN_IMAGES } from './TargetSilhouette';
import MapMenuPanel from './MapMenuPanel';
import CrosshairSelector, { CrosshairStyle } from './CrosshairSelector';
import SimulationPanel, { SimulationSettings, DEFAULT_SIMULATION_SETTINGS } from './SimulationPanel';
import Link from 'next/link';

export type RoundState = 'waiting' | 'spawning' | 'active' | 'resolving';
export type RoundResult = 'hit' | 'miss' | 'early' | null;

export interface SessionStats {
  streak: number;
  bestStreak: number;
  totalHits: number;
  totalMisses: number;
  totalBodyHits: number;
  lastReactionMs: number | null;
  bestReactionMs: number | null;
}

const TIMING_WINDOW_MS = 450;

// All target skin image paths for preloading
const ALL_SKIN_IMAGES: string[] = Object.values(SKIN_IMAGES).flat().filter(Boolean);

// Module-level image cache — keeps Image objects alive so the browser retains decoded bitmaps
const _preloadedImageCache: Map<string, HTMLImageElement> = new Map();

const DEFAULT_SETTINGS: TrainerSettings = {
  sensitivity: 1.0,
  windowMs: 750,
  crosshairSize: 4,
  crosshairColor: '#FFFFFF',
  showFlash: true,
  showGun: false,
  targetScale: 3.5,
  aimDetectionSource: 'crosshair',
  showDebugHitboxes: false,
  characterSpeed: 300,
  travelDistance: 60,
  targetMotion: 'pass-through',
  spawnDirection: 'left',
  targetSkin: 'red-outline',
  targetSkinWeapon: 'vandal',
  missedShotBehavior: 'disappear',
  intervalsEnabled: true,
};

const SPAWN_OFFSET_STORAGE_KEY = 'aimtrainer_spawn_offsets';
const ADVANCED_HITBOX_STORAGE_KEY = 'aimtrainer_advanced_hitboxes';
const CROSSHAIR_STYLE_STORAGE_KEY = 'aimtrainer_crosshair_style';
const SETTINGS_STORAGE_KEY = 'aimtrainer_settings';
const SIMULATION_SETTINGS_STORAGE_KEY = 'aimtrainer_simulation_settings';
const SETTINGS_HINT_SEEN_KEY = 'aimtrainer_settings_hint_seen';

function offsetKey(mapId: string): string {
  return mapId;
}

function loadSpawnOffsets(): Record<string, SpawnOffset> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(SPAWN_OFFSET_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveSpawnOffsets(offsets: Record<string, SpawnOffset>) {
  try {
    localStorage.setItem(SPAWN_OFFSET_STORAGE_KEY, JSON.stringify(offsets));
  } catch { /* ignore */ }
}

function loadAdvancedHitboxes(): Record<string, AdvancedHitbox> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(ADVANCED_HITBOX_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveAdvancedHitboxes(hitboxes: Record<string, AdvancedHitbox>) {
  try {
    localStorage.setItem(ADVANCED_HITBOX_STORAGE_KEY, JSON.stringify(hitboxes));
  } catch { /* ignore */ }
}

function loadCrosshairStyle(): CrosshairStyle {
  if (typeof window === 'undefined') return 'dot';
  try {
    const raw = localStorage.getItem(CROSSHAIR_STYLE_STORAGE_KEY);
    if (raw === 'dot' || raw === 'classic') return raw;
  } catch { /* ignore */ }
  return 'dot';
}

function saveCrosshairStyle(style: CrosshairStyle) {
  try {
    localStorage.setItem(CROSSHAIR_STYLE_STORAGE_KEY, style);
  } catch { /* ignore */ }
}

function loadSettings(): Partial<TrainerSettings> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveSettings(s: TrainerSettings) {
  try {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(s));
  } catch { /* ignore */ }
}

function loadSimulationSettings(): SimulationSettings {
  if (typeof window === 'undefined') return DEFAULT_SIMULATION_SETTINGS;
  try {
    const raw = localStorage.getItem(SIMULATION_SETTINGS_STORAGE_KEY);
    if (!raw) return DEFAULT_SIMULATION_SETTINGS;
    const parsed = JSON.parse(raw);
    // Migrate: 'overlapping' was removed — reset to 'fully-random'
    if (parsed.timingMode === 'overlapping') {
      parsed.timingMode = 'fully-random';
    }
    // Migrate: add firstSpawnSide if missing from old saves
    if (!parsed.firstSpawnSide) {
      parsed.firstSpawnSide = 'random';
    }
    return { ...DEFAULT_SIMULATION_SETTINGS, ...parsed };
  } catch { return DEFAULT_SIMULATION_SETTINGS; }
}

function saveSimulationSettings(s: SimulationSettings) {
  try {
    localStorage.setItem(SIMULATION_SETTINGS_STORAGE_KEY, JSON.stringify(s));
  } catch { /* ignore */ }
}

// Only angle-based maps (with backgroundImage) — sorted alphabetically by name
const ANGLE_MAPS = MAP_CONFIGS
  .filter((m) => !!m.backgroundImage)
  .sort((a, b) => a.name.localeCompare(b.name));

const INITIAL_MAP = ANGLE_MAPS[0] ?? MAP_CONFIGS[0];

// ── Panel IDs for collision detection ────────────────────────────────────────
type PanelId = 'core' | 'advanced' | 'map' | 'crosshair' | 'simulation';

// Panel layout positions (approximate screen quadrant)
// Used to determine which panels might overlap
const PANEL_POSITIONS: Record<PanelId, { corner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' }> = {
  core:       { corner: 'top-left' },
  map:        { corner: 'top-right' },
  advanced:   { corner: 'bottom-right' },
  crosshair:  { corner: 'bottom-right' },
  simulation: { corner: 'bottom-left' },
};

// Two panels conflict if they share the same corner
function panelsConflict(a: PanelId, b: PanelId): boolean {
  return PANEL_POSITIONS[a].corner === PANEL_POSITIONS[b].corner;
}

export default function AimTrainerApp() {
  const [selectedMap, setSelectedMap] = useState<MapConfig | null>(null);
  const [selectedAngleBg, setSelectedAngleBg] = useState<string | undefined>(undefined);
  const [settings, setSettings] = useState<TrainerSettings>(DEFAULT_SETTINGS);
  const [simulationSettings, setSimulationSettings] = useState<SimulationSettings>(DEFAULT_SIMULATION_SETTINGS);
  const [isRunning, setIsRunning] = useState(false);
  const [showPressStart, setShowPressStart] = useState(true);
  const [pressStartFading, setPressStartFading] = useState(false);
  const [showCrosshair, setShowCrosshair] = useState(false);
  const [skinsPreloaded, setSkinsPreloaded] = useState(false);
  const [showSettingsHint, setShowSettingsHint] = useState(false);

  const [spawnOffsets, setSpawnOffsets] = useState<Record<string, SpawnOffset>>({});
  const [advancedHitboxes, setAdvancedHitboxes] = useState<Record<string, AdvancedHitbox>>({});
  const [crosshairStyle, setCrosshairStyle] = useState<CrosshairStyle>('dot');

  // ── Panel open state ────────────────────────────────────────────────────────
  // Each panel has two states only: closed (not in openPanels) or open
  const [openPanels, setOpenPanels] = useState<Set<PanelId>>(new Set());

  // Toggle a panel open/closed
  const togglePanel = useCallback((panelId: PanelId) => {
    setOpenPanels(prev => {
      const next = new Set(prev);
      if (next.has(panelId)) {
        next.delete(panelId);
      } else {
        next.add(panelId);
      }
      return next;
    });
  }, []);

  // Dismiss settings hint and persist
  const dismissSettingsHint = useCallback(() => {
    setShowSettingsHint(false);
    try { localStorage.setItem(SETTINGS_HINT_SEEN_KEY, '1'); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    // Check if first visit for settings hint
    try {
      const seen = localStorage.getItem(SETTINGS_HINT_SEEN_KEY);
      if (!seen) setShowSettingsHint(true);
    } catch { /* ignore */ }
    // Randomize the starting map/angle on every page load
    const randomIndex = Math.floor(Math.random() * ANGLE_MAPS.length);
    const randomMap = ANGLE_MAPS[randomIndex] ?? INITIAL_MAP;
    setSelectedMap(randomMap);
    setSelectedAngleBg(randomMap.backgroundImage);

    setSpawnOffsets(loadSpawnOffsets());
    setAdvancedHitboxes(loadAdvancedHitboxes());
    setCrosshairStyle(loadCrosshairStyle());
    const saved = loadSettings();
    if (Object.keys(saved).length > 0) {
      setSettings(prev => ({ ...prev, ...saved, crosshairColor: '#FFFFFF' }));
    }
    setSimulationSettings(loadSimulationSettings());

    // Preload all target skin images with full decode confirmation before gameplay
    const total = ALL_SKIN_IMAGES.length;
    if (total === 0) {
      setSkinsPreloaded(true);
      return;
    }
    let loadedCount = 0;
    ALL_SKIN_IMAGES.forEach((src) => {
      const img = new Image();
      img.src = src;
      // Store in module-level cache so the decoded bitmap stays in memory
      _preloadedImageCache.set(src, img);
      img.decode().then(() => {
        loadedCount += 1;
        if (loadedCount >= total) {
          setSkinsPreloaded(true);
        }
      }).catch(() => {
        loadedCount += 1;
        if (loadedCount >= total) {
          setSkinsPreloaded(true);
        }
      });
    });
  }, []);

  const [roundState, setRoundState] = useState<RoundState>('waiting');
  const [roundResult, setRoundResult] = useState<RoundResult>(null);
  const [stats, setStats] = useState<SessionStats>({
    streak: 0, bestStreak: 0, totalHits: 0, totalMisses: 0, totalBodyHits: 0,
    lastReactionMs: null, bestReactionMs: null,
  });
  const [showHeadshotRing, setShowHeadshotRing] = useState(false);

  // ── Second target state (Simulation Mode) ────────────────────────────────
  const [secondRoundState, setSecondRoundState] = useState<RoundState>('waiting');
  const [secondTargetSpawnSide, setSecondTargetSpawnSide] = useState<'left' | 'right'>('left');
  // Primary target spawn side (used when Simulation Mode firstSpawnSide = 'random')
  const [primarySimSpawnSide, setPrimarySimSpawnSide] = useState<'left' | 'right'>('left');
  // Independent X position for the second target (% of viewport width)
  const [secondTargetXPercent, setSecondTargetXPercent] = useState<number>(25);

  // ── Web Animations API flash controller ───────────────────────────────────
  const flashOverlayRef = useRef<HTMLDivElement | null>(null);
  const flashAnimRef = useRef<Animation | null>(null);

  const triggerFlash = useCallback((type: 'hit' | 'miss') => {
    const el = flashOverlayRef.current;
    if (!el) return;
    if (flashAnimRef.current) {
      flashAnimRef.current.cancel();
      flashAnimRef.current = null;
    }
    const isHit = type === 'hit';
    const color = isHit ? 'rgba(0,255,148,0.10)' : 'rgba(255,70,85,0.15)';
    const startOpacity = isHit ? 0.5 : 0.4;
    const durationMs = isHit ? 300 : 350;
    const keyframes: Keyframe[] = isHit
      ? [{ opacity: startOpacity, background: color }, { opacity: 0, background: color }]
      : [{ opacity: startOpacity, background: color }, { opacity: 0.25, background: color }, { opacity: 0, background: color }];
    const anim = el.animate(keyframes, { duration: durationMs, easing: 'ease-out', fill: 'forwards' });
    flashAnimRef.current = anim;
    anim.onfinish = () => {
      el.style.opacity = '0';
      if (flashAnimRef.current === anim) flashAnimRef.current = null;
    };
    anim.oncancel = () => {
      el.style.opacity = '0';
    };
  }, []);

  const flashReadyRef = useRef(false);

  const sessionHitLogCountRef = useRef(0);
  const SESSION_HIT_LOG_LIMIT = 20;

  function logFlashAttempt(
    outcome: 'head' | 'body' | 'miss',
    showFlash: boolean,
    flashReady: boolean,
  ) {
    if (sessionHitLogCountRef.current >= SESSION_HIT_LOG_LIMIT) return;
    sessionHitLogCountRef.current += 1;
    const hitNum = sessionHitLogCountRef.current;
    const ts = performance.now().toFixed(1);
    const willFire = showFlash && flashReady;
    const status = willFire ? 'FIRED' : `SKIPPED(showFlash=${showFlash},flashReady=${flashReady})`;
    console.log(`[HitFlash] #${hitNum} t=${ts}ms | outcome=${outcome} | ${status}`);
  }

  const spawnTimeRef = useRef<number>(0);

  const roundStateRef = useRef<RoundState>('waiting');
  const loopIdRef = useRef<number>(0);

  const respawnDelayRef   = useRef(settings.windowMs);
  const characterSpeedRef = useRef(settings.characterSpeed);
  const targetMotionRef   = useRef(settings.targetMotion);
  const missedShotBehaviorRef = useRef(settings.missedShotBehavior);
  const intervalsEnabledRef = useRef(settings.intervalsEnabled);
  respawnDelayRef.current   = settings.windowMs;
  characterSpeedRef.current = settings.characterSpeed;
  targetMotionRef.current   = settings.targetMotion;
  missedShotBehaviorRef.current = settings.missedShotBehavior;
  intervalsEnabledRef.current = settings.intervalsEnabled;

  const isRunningRef = useRef(false);

  const pendingTimers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const pendingFrames = useRef<Set<number>>(new Set());

  // ── Simulation mode refs ──────────────────────────────────────────────────
  const simulationEnabledRef = useRef(simulationSettings.enabled);
  const simulationTimingRef = useRef(simulationSettings.timingMode);
  const simulationSpawnSideRef = useRef(simulationSettings.spawnSide);
  const simulationFirstSpawnSideRef = useRef(simulationSettings.firstSpawnSide);
  simulationEnabledRef.current = simulationSettings.enabled;
  simulationTimingRef.current = simulationSettings.timingMode;
  simulationSpawnSideRef.current = simulationSettings.spawnSide;
  simulationFirstSpawnSideRef.current = simulationSettings.firstSpawnSide;

  // Second target round management
  const secondLoopIdRef = useRef<number>(0);
  const secondPendingTimers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const secondPendingFrames = useRef<Set<number>>(new Set());
  const secondRoundStateRef = useRef<RoundState>('waiting');
  const resolveSecondRoundRef = useRef<(outcome: 'head' | 'body' | 'miss') => void>(() => {});
  const secondSpawnTimeRef = useRef<number>(0);

  const cancelSecondRound = useCallback(() => {
    secondLoopIdRef.current += 1;
    secondPendingTimers.current.forEach(id => clearTimeout(id));
    secondPendingTimers.current.clear();
    secondPendingFrames.current.forEach(id => cancelAnimationFrame(id));
    secondPendingFrames.current.clear();
    setSecondRoundState('waiting');
    secondRoundStateRef.current = 'waiting';
  }, []);

  const safeSecondTimeout = useCallback((fn: () => void, ms: number, capturedId: number) => {
    const id = setTimeout(() => {
      secondPendingTimers.current.delete(id);
      if (secondLoopIdRef.current !== capturedId) return;
      fn();
    }, ms);
    secondPendingTimers.current.add(id);
    return id;
  }, []);

  const safeSecondRaf = useCallback((fn: () => void, capturedId: number) => {
    const id = requestAnimationFrame(() => {
      secondPendingFrames.current.delete(id);
      if (secondLoopIdRef.current !== capturedId) return;
      fn();
    });
    secondPendingFrames.current.add(id);
    return id;
  }, []);

  const cancelCurrentRound = useCallback(() => {
    loopIdRef.current += 1;
    pendingTimers.current.forEach(id => clearTimeout(id));
    pendingTimers.current.clear();
    pendingFrames.current.forEach(id => cancelAnimationFrame(id));
    pendingFrames.current.clear();
  }, []);

  const safeTimeout = useCallback((fn: () => void, ms: number, capturedId: number) => {
    const id = setTimeout(() => {
      pendingTimers.current.delete(id);
      if (loopIdRef.current !== capturedId) return;
      fn();
    }, ms);
    pendingTimers.current.add(id);
    return id;
  }, []);

  const safeRaf = useCallback((fn: () => void, capturedId: number) => {
    const id = requestAnimationFrame(() => {
      pendingFrames.current.delete(id);
      if (loopIdRef.current !== capturedId) return;
      fn();
    });
    pendingFrames.current.add(id);
    return id;
  }, []);

  const resolveCurrentRoundRef = useRef<(outcome: 'head' | 'body' | 'miss', reactionMs: number | null) => void>(() => {});
  const currentRoundIdRef = useRef<number>(-1);

  const showFlashRef = useRef(settings.showFlash);
  showFlashRef.current = settings.showFlash;

  // ── Second target round — exact 1:1 copy of startRound's code path ─────────
  // Uses the same safeSecondTimeout/safeSecondRaf infrastructure, same state
  // transitions, same auto-restart loop. Only differences: spawn side comes from
  // simulationSpawnSideRef, initial delay is passed in, and it uses second* refs.
  const startSecondRound = useCallback((delayMs: number) => {
    if (!isRunningRef.current) return;
    // Do not start if simulation has been disabled
    if (!simulationEnabledRef.current) return;

    secondLoopIdRef.current += 1;
    const myId = secondLoopIdRef.current;

    // Determine spawn side — exact same logic as primary's effectiveSpawnSide
    const sideSetting = simulationSpawnSideRef.current;
    const side: 'left' | 'right' = sideSetting === 'random'
      ? (Math.random() < 0.5 ? 'left' : 'right')
      : sideSetting;
    setSecondTargetSpawnSide(side);

    // Assign a random independent X position for the second target
    // Left side: 18–32% of viewport width; Right side: 68–82%
    const xPercent = side === 'left'
      ? Math.round(18 + Math.random() * 14)
      : Math.round(68 + Math.random() * 14);
    setSecondTargetXPercent(xPercent);

    setSecondRoundState('waiting');
    secondRoundStateRef.current = 'waiting';

    safeSecondTimeout(() => {
      if (!simulationEnabledRef.current) return;
      setSecondRoundState('spawning');
      secondRoundStateRef.current = 'spawning';

      const peekDurationMs = Math.round(Math.max(50, Math.min(2000, characterSpeedRef.current)));

      safeSecondTimeout(() => {
        safeSecondRaf(() => {
          if (!simulationEnabledRef.current) return;
          secondSpawnTimeRef.current = performance.now();
          setSecondRoundState('active');
          secondRoundStateRef.current = 'active';

          safeSecondTimeout(() => {
            resolveSecondRoundWithId(myId, 'miss', null);
          }, TIMING_WINDOW_MS, myId);
        }, myId);
      }, peekDurationMs, myId);

    }, delayMs, myId);

    function resolveSecondRoundWithId(
      roundId: number,
      outcome: 'head' | 'body' | 'miss',
      reactionMs: number | null,
    ) {
      if (secondLoopIdRef.current !== roundId) return;

      secondLoopIdRef.current += 1;
      const nextId = secondLoopIdRef.current;

      setSecondRoundState('resolving');
      secondRoundStateRef.current = 'resolving';

      if (outcome === 'head') {
        logFlashAttempt('head', showFlashRef.current, flashReadyRef.current);
        if (showFlashRef.current && flashReadyRef.current) {
          triggerFlash('hit');
        }
        setShowHeadshotRing(true);
        setStats(prev => {
          const newStreak = prev.streak + 1;
          return {
            ...prev,
            streak: newStreak,
            bestStreak: Math.max(prev.bestStreak, newStreak),
            totalHits: prev.totalHits + 1,
            lastReactionMs: reactionMs,
            bestReactionMs: prev.bestReactionMs === null ? reactionMs : (reactionMs !== null ? Math.min(prev.bestReactionMs, reactionMs) : prev.bestReactionMs),
          };
        });
        safeSecondTimeout(() => {
          setShowHeadshotRing(false);
        }, 300, nextId);
      } else if (outcome === 'body') {
        logFlashAttempt('body', showFlashRef.current, flashReadyRef.current);
        if (showFlashRef.current && flashReadyRef.current) {
          triggerFlash('hit');
        }
        setStats(prev => ({ ...prev, streak: 0, totalBodyHits: prev.totalBodyHits + 1, lastReactionMs: reactionMs }));
      } else {
        // Only count as miss when the target finishes its travel (timer-based resolve)
        // reactionMs is null for timer-based resolves, non-null for click-based
        if (reactionMs === null) {
          logFlashAttempt('miss', showFlashRef.current, flashReadyRef.current);
          if (showFlashRef.current && flashReadyRef.current) {
            triggerFlash('miss');
          }
          setStats(prev => ({ ...prev, streak: 0, totalMisses: prev.totalMisses + 1, lastReactionMs: null }));
        }
      }

      // Auto-restart secondary round — only if simulation is still enabled
      safeSecondTimeout(() => {
        if (!isRunningRef.current) return;
        if (!simulationEnabledRef.current) return;
        // Fully Random: randomize the inter-target delay each restart
        const minDelay = 50;
        const maxDelay = Math.max(200, respawnDelayRef.current * 1.5);
        const nextDelay = Math.round(minDelay + Math.random() * (maxDelay - minDelay));
        startSecondRound(nextDelay);
      }, respawnDelayRef.current, nextId);
    }

    resolveSecondRoundRef.current = (outcome: 'head' | 'body' | 'miss') => {
      const reactionMs = secondRoundStateRef.current === 'active'
        ? Math.round(performance.now() - secondSpawnTimeRef.current)
        : null;
      resolveSecondRoundWithId(myId, outcome, reactionMs);
    };
  }, [safeSecondTimeout, safeSecondRaf, triggerFlash]); // eslint-disable-line react-hooks/exhaustive-deps

  const startRound = useCallback(() => {
    if (!isRunningRef.current) return;

    loopIdRef.current += 1;
    const myId = loopIdRef.current;

    flashReadyRef.current = false;

    setRoundResult(null);
    setRoundState('waiting');
    roundStateRef.current = 'waiting';

    const baseDelay = respawnDelayRef.current;
    const effectiveDelay = intervalsEnabledRef.current
      ? Math.round(1 + Math.random() * (baseDelay - 1))
      : baseDelay;

    safeTimeout(() => {
      setRoundState('spawning');
      roundStateRef.current = 'spawning';

      const peekDurationMs = Math.round(Math.max(50, Math.min(2000, characterSpeedRef.current)));

      // ── Simulation Mode: resolve primary spawn side per-round ─────────────
      if (simulationEnabledRef.current) {
        const firstSide = simulationFirstSpawnSideRef.current;
        const resolvedPrimary: 'left' | 'right' = firstSide === 'random'
          ? (Math.random() < 0.5 ? 'left' : 'right')
          : firstSide;
        setPrimarySimSpawnSide(resolvedPrimary);
      }

      const isPassThrough = targetMotionRef.current === 'pass-through';

      if (isPassThrough) {
        safeTimeout(() => {
          safeRaf(() => {
            spawnTimeRef.current = performance.now();
            setRoundState('active');
            roundStateRef.current = 'active';

            safeTimeout(() => {
              resolveRoundWithId(myId, 'miss', null);
            }, TIMING_WINDOW_MS, myId);
          }, myId);
        }, peekDurationMs, myId);

      } else {
        safeTimeout(() => {
          safeRaf(() => {
            spawnTimeRef.current = performance.now();
            setRoundState('active');
            roundStateRef.current = 'active';

            safeTimeout(() => {
              resolveRoundWithId(myId, 'miss', null);
            }, TIMING_WINDOW_MS, myId);
          }, myId);
        }, peekDurationMs, myId);
      }

      // ── Simulation Mode: schedule second target only if not already running ─
      if (simulationEnabledRef.current) {
        const secondIsRunning = secondRoundStateRef.current === 'spawning' || secondRoundStateRef.current === 'active';
        if (!secondIsRunning) {
          const minDelay = 50;
          const maxDelay = Math.max(200, respawnDelayRef.current * 1.5);
          const secondDelay = Math.round(minDelay + Math.random() * (maxDelay - minDelay));
          startSecondRound(secondDelay);
        }
      }

    }, effectiveDelay, myId);

    function resolveRoundWithId(
      roundId: number,
      outcome: 'head' | 'body' | 'miss',
      reactionMs: number | null,
    ) {
      if (loopIdRef.current !== roundId) return;

      loopIdRef.current += 1;
      const nextId = loopIdRef.current;

      setRoundState('resolving');
      roundStateRef.current = 'resolving';

      if (outcome === 'head') {
        setRoundResult('hit');
        logFlashAttempt('head', showFlashRef.current, flashReadyRef.current);
        if (showFlashRef.current && flashReadyRef.current) {
          triggerFlash('hit');
        }
        setShowHeadshotRing(true);
        setStats(prev => {
          const newStreak = prev.streak + 1;
          return {
            ...prev,
            streak: newStreak,
            bestStreak: Math.max(prev.bestStreak, newStreak),
            totalHits: prev.totalHits + 1,
            lastReactionMs: reactionMs,
            bestReactionMs: prev.bestReactionMs === null ? reactionMs : (reactionMs !== null ? Math.min(prev.bestReactionMs, reactionMs) : prev.bestReactionMs),
          };
        });
        safeTimeout(() => {
          setShowHeadshotRing(false);
        }, 300, nextId);
      } else if (outcome === 'body') {
        setRoundResult('miss');
        logFlashAttempt('body', showFlashRef.current, flashReadyRef.current);
        if (showFlashRef.current && flashReadyRef.current) {
          triggerFlash('hit');
        }
        setStats(prev => ({ ...prev, streak: 0, totalBodyHits: prev.totalBodyHits + 1, lastReactionMs: reactionMs }));
      } else {
        setRoundResult('miss');
        logFlashAttempt('miss', showFlashRef.current, flashReadyRef.current);
        if (showFlashRef.current && flashReadyRef.current) {
          triggerFlash('miss');
        }
        setStats(prev => ({ ...prev, streak: 0, totalMisses: prev.totalMisses + 1, lastReactionMs: null }));
      }

      safeTimeout(() => {
        if (!isRunningRef.current) return;
        startRound();
      }, respawnDelayRef.current, nextId);
    }

    resolveCurrentRoundRef.current = (outcome: 'head' | 'body' | 'miss', reactionMs: number | null) => {
      resolveRoundWithId(myId, outcome, reactionMs);
    };
    currentRoundIdRef.current = myId;
    flashReadyRef.current = true;
  }, [safeTimeout, safeRaf, startSecondRound, triggerFlash]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTargetClick = useCallback((hitZone: 'head' | 'body' | 'miss') => {
    if (!isRunningRef.current) return;

    const reactionMs = roundStateRef.current === 'active'
      ? Math.round(performance.now() - spawnTimeRef.current)
      : null;

    if (roundStateRef.current === 'active' || roundStateRef.current === 'spawning') {
      // With 'leave' behavior, a click-miss does NOT resolve the round —
      // the target continues its travel and the timer will resolve it naturally.
      if (hitZone === 'miss' && missedShotBehaviorRef.current === 'leave') {
        return;
      }
      resolveCurrentRoundRef.current(hitZone, reactionMs);
    }
  }, []);

  // Handler for second target clicks — exact same pattern as handleTargetClick
  const handleSecondTargetClick = useCallback((hitZone: 'head' | 'body' | 'miss') => {
    if (!isRunningRef.current) return;
    if (secondRoundStateRef.current === 'active' || secondRoundStateRef.current === 'spawning') {
      // With 'leave' behavior, a click-miss does NOT resolve the round
      if (hitZone === 'miss' && missedShotBehaviorRef.current === 'leave') return;
      resolveSecondRoundRef.current(hitZone);
    }
  }, []);

  const handleSceneClick = useCallback(() => {
    if (!isRunningRef.current) return;
    if (roundStateRef.current === 'active' || roundStateRef.current === 'spawning') {
      // With 'leave' behavior, clicking outside the target does not count as a miss
      if (missedShotBehaviorRef.current === 'leave') return;
      handleTargetClick('miss');
    }
  }, [handleTargetClick]);

  useEffect(() => {
    if (isRunning) {
      isRunningRef.current = true;
      startRound();
    }
    return () => {
      isRunningRef.current = false;
      cancelCurrentRound();
      cancelSecondRound();
      roundStateRef.current = 'waiting';
    };
  }, [isRunning]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMapChange = useCallback((map: MapConfig, backgroundImage?: string) => {
    isRunningRef.current = false;
    cancelCurrentRound();
    cancelSecondRound();
    flashReadyRef.current = false;
    if (flashAnimRef.current) { flashAnimRef.current.cancel(); flashAnimRef.current = null; }
    setSelectedMap(map);
    setSelectedAngleBg(backgroundImage);
    setStats({ streak: 0, bestStreak: 0, totalHits: 0, totalMisses: 0, totalBodyHits: 0, lastReactionMs: null, bestReactionMs: null });
    setRoundState('waiting');
    roundStateRef.current = 'waiting';
    setSecondRoundState('waiting');
    secondRoundStateRef.current = 'waiting';
    setRoundResult(null);
    setIsRunning(false);
    setShowCrosshair(false);
    setPressStartFading(false);
    setShowPressStart(true);
  }, [cancelCurrentRound, cancelSecondRound]);

  const handleReset = useCallback(() => {
    cancelCurrentRound();
    cancelSecondRound();
    setStats({ streak: 0, bestStreak: 0, totalHits: 0, totalMisses: 0, totalBodyHits: 0, lastReactionMs: null, bestReactionMs: null });
    setRoundState('waiting');
    roundStateRef.current = 'waiting';
    setSecondRoundState('waiting');
    secondRoundStateRef.current = 'waiting';
    setRoundResult(null);
    if (isRunning) {
      startRound();
    }
  }, [cancelCurrentRound, cancelSecondRound, isRunning, startRound]);

  const handleStartStop = useCallback(() => {
    if (isRunning) {
      isRunningRef.current = false;
      cancelCurrentRound();
      cancelSecondRound();
      if (flashAnimRef.current) { flashAnimRef.current.cancel(); flashAnimRef.current = null; }
      setRoundState('waiting');
      roundStateRef.current = 'waiting';
      setSecondRoundState('waiting');
      secondRoundStateRef.current = 'waiting';
      setRoundResult(null);
      flashReadyRef.current = false;
      sessionHitLogCountRef.current = 0;
      setIsRunning(false);
      setShowCrosshair(false);
      setPressStartFading(false);
      setShowPressStart(true);
    } else {
      if (!skinsPreloaded) return;
      sessionHitLogCountRef.current = 0;
      flashReadyRef.current = false;
      setPressStartFading(true);
      setTimeout(() => {
        setShowPressStart(false);
        setPressStartFading(false);
        setShowCrosshair(true);
        isRunningRef.current = true;
        setIsRunning(true);
      }, 600);
    }
  }, [isRunning, cancelCurrentRound, cancelSecondRound, skinsPreloaded]);

  const handleGameAreaClick = useCallback(() => {
    if (!isRunning && showPressStart && !pressStartFading && skinsPreloaded) {
      handleStartStop();
    }
  }, [isRunning, showPressStart, pressStartFading, handleStartStop]);

  const accuracy = stats.totalHits + stats.totalMisses > 0
    ? Math.round((stats.totalHits / (stats.totalHits + stats.totalMisses)) * 100)
    : 0;

  const effectiveMap: MapConfig | null = selectedMap ? {
    ...selectedMap,
    windowMs: settings.windowMs,
    backgroundImage: selectedAngleBg ?? selectedMap.backgroundImage,
  } : null;

  const currentOffsetKey = selectedMap ? offsetKey(selectedMap.id) : '';
  const currentSpawnOffset: SpawnOffset = spawnOffsets[currentOffsetKey] ?? { x: 0, y: 0 };
  const currentAdvancedHitbox: AdvancedHitbox = advancedHitboxes[currentOffsetKey] ?? DEFAULT_ADVANCED_HITBOX;

  const handleSpawnOffsetChange = useCallback((offset: SpawnOffset) => {
    setSpawnOffsets(prev => {
      const next = { ...prev, [currentOffsetKey]: offset };
      saveSpawnOffsets(next);
      return next;
    });
  }, [currentOffsetKey]);

  const handleSpawnOffsetReset = useCallback(() => {
    setSpawnOffsets(prev => {
      const next = { ...prev, [currentOffsetKey]: { x: 0, y: 0 } };
      saveSpawnOffsets(next);
      return next;
    });
  }, [currentOffsetKey]);

  const handleAdvancedHitboxChange = useCallback((hitbox: AdvancedHitbox) => {
    setAdvancedHitboxes(prev => {
      const next = { ...prev, [currentOffsetKey]: hitbox };
      saveAdvancedHitboxes(next);
      return next;
    });
  }, [currentOffsetKey]);

  const handleAdvancedHitboxReset = useCallback(() => {
    setAdvancedHitboxes(prev => {
      const next = { ...prev, [currentOffsetKey]: { ...DEFAULT_ADVANCED_HITBOX } };
      saveAdvancedHitboxes(next);
      return next;
    });
  }, [currentOffsetKey]);

  const handleCrosshairStyleChange = useCallback((style: CrosshairStyle) => {
    setCrosshairStyle(style);
    saveCrosshairStyle(style);
  }, []);

  const handleSettingsChange = useCallback((newSettings: TrainerSettings) => {
    setSettings(newSettings);
    saveSettings(newSettings);
  }, []);

  const handleSimulationSettingsChange = useCallback((newSettings: SimulationSettings) => {
    setSimulationSettings(newSettings);
    saveSimulationSettings(newSettings);
    // If simulation is being turned off, immediately cancel the second round
    if (!newSettings.enabled) {
      cancelSecondRound();
    }
  }, [cancelSecondRound]);

  // The second target is visible whenever simulation is enabled AND it's in an active state
  const isSimulationTargetVisible = simulationSettings.enabled &&
    (secondRoundState === 'spawning' || secondRoundState === 'active' || secondRoundState === 'resolving');

  // When Simulation Mode is on, the primary target's spawn side is controlled by firstSpawnSide
  const effectiveSpawnDirection: 'left' | 'right' | undefined = simulationSettings.enabled
    ? (simulationSettings.firstSpawnSide === 'random' ? primarySimSpawnSide : simulationSettings.firstSpawnSide)
    : settings.spawnDirection;

  return (
    <div className="w-screen h-screen overflow-hidden relative select-none" style={{ cursor: 'crosshair' }}>
      {/* Black screen shown until random map is selected */}
      {!selectedMap && (
        <div className="absolute inset-0 bg-black z-50" />
      )}
      {/* Game scene — full screen */}
      {effectiveMap && (
      <GameScene
        map={effectiveMap}
        roundState={roundState}
        roundResult={roundResult}
        onTargetClick={handleTargetClick}
        onSceneClick={handleSceneClick}
        targetScale={settings.targetScale}
        showGun={false}
        crosshairSize={settings.crosshairSize}
        crosshairColor={settings.crosshairColor}
        showCrosshair={showCrosshair}
        crosshairStyle={crosshairStyle}
        spawnOffsetX={currentSpawnOffset.x}
        spawnOffsetY={currentSpawnOffset.y}
        aimDetectionSource={settings.aimDetectionSource}
        showDebugHitboxes={settings.showDebugHitboxes}
        characterSpeed={settings.characterSpeed}
        travelDistance={settings.travelDistance}
        targetMotion={settings.targetMotion}
        spawnDirection={effectiveSpawnDirection}
        targetSkin={settings.targetSkin}
        targetSkinWeapon={settings.targetSkinWeapon}
        advancedHitbox={currentAdvancedHitbox}
        missedShotBehavior={settings.missedShotBehavior}
        secondTargetVisible={isSimulationTargetVisible}
        secondTargetSpawnSide={secondTargetSpawnSide}
        secondRoundState={secondRoundState}
        onSecondTargetClick={handleSecondTargetClick}
        secondTargetXPercent={secondTargetXPercent}
      />
      )}

      {/* Press Start to Begin overlay */}
      {showPressStart && (
        <div
          className="absolute inset-0 flex items-center justify-center z-30"
          style={{
            background: 'rgba(0,0,0,0.35)',
            opacity: pressStartFading ? 0 : 1,
            transition: pressStartFading ? 'opacity 0.6s ease' : 'none',
            cursor: skinsPreloaded ? 'pointer' : 'default',
          }}
          onClick={handleGameAreaClick}
        >
          <div className="flex flex-col items-center gap-3">
            {!skinsPreloaded ? (
              <span
                className="text-xs uppercase tracking-widest font-semibold"
                style={{ color: 'rgba(255,255,255,0.45)', letterSpacing: '0.22em', fontSize: '13px' }}
              >
                Loading assets…
              </span>
            ) : (
              <span
                className="text-xs uppercase tracking-widest font-semibold"
                style={{ color: 'rgba(255,255,255,0.55)', letterSpacing: '0.22em', fontSize: '13px' }}
              >
                Press Start to Begin
              </span>
            )}
          </div>
        </div>
      )}

      {/* Vignette */}
      <div className="absolute inset-0 vignette pointer-events-none z-10" />

      {/* Hit/Miss flash overlay */}
      <div
        ref={flashOverlayRef}
        className="absolute inset-0 pointer-events-none z-20"
        style={{ opacity: 0 }}
      />

      {/* Headshot ring */}
      {showHeadshotRing && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
          <div
            className="w-16 h-16 rounded-full border-2 animate-pulse-ring"
            style={{ borderColor: 'var(--hit-green)', boxShadow: '0 0 20px var(--hit-green)' }}
          />
        </div>
      )}

      {/* ── Apple-style top HUD bar ── */}
      <div
        className="absolute top-4 z-50 pointer-events-none"
        style={{
          left: '50%',
          transform: 'translateX(-50%)',
          maxWidth: 'calc(100vw - 100px)',
        }}
      >
        <div
          className="flex items-center gap-2 sm:gap-5 px-3 sm:px-5 py-2 sm:py-2.5 rounded-2xl"
          style={{
            background: 'rgba(12, 12, 18, 0.70)',
            backdropFilter: 'blur(24px) saturate(180%)',
            WebkitBackdropFilter: 'blur(24px) saturate(180%)',
            border: '1px solid rgba(255,255,255,0.09)',
            boxShadow: '0 4px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.07)',
            whiteSpace: 'nowrap',
          }}
        >
          {/* Menu button — next to Streak */}
          <Link
            href="/"
            className="pointer-events-auto flex items-center gap-1 px-2 py-1 rounded-lg transition-all duration-150"
            style={{
              color: 'rgba(255,255,255,0.5)',
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              fontSize: '10px',
              letterSpacing: '0.08em',
              fontFamily: 'var(--font-sans)',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.85)';
              (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(255,255,255,0.1)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.5)';
              (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(255,255,255,0.06)';
            }}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
            MENU
          </Link>
          <HudDivider />
          <HudStat
            label="STREAK"
            value={String(stats.streak)}
            color={stats.streak >= 5 ? 'var(--streak-gold)' : stats.streak > 0 ? 'rgba(232,232,240,0.95)' : 'rgba(255,255,255,0.3)'}
            large
            glow={stats.streak >= 5}
          />
          <HudDivider />
          <span className="hidden sm:contents">
            <HudStat label="BEST" value={String(stats.bestStreak)} color="rgba(232,232,240,0.7)" />
            <HudDivider />
          </span>
          <HudStat label="HITS" value={String(stats.totalHits)} color="var(--hit-green)" />
          <span className="hidden sm:contents">
            <HudStat label="BODY" value={String(stats.totalBodyHits)} color="var(--warn-amber)" />
          </span>
          <HudStat label="MISS" value={String(stats.totalMisses)} color="var(--accent)" />
          <HudDivider />
          <HudStat
            label="ACC"
            value={`${accuracy}%`}
            color={accuracy >= 70 ? 'var(--hit-green)' : accuracy >= 40 ? 'var(--warn-amber)' : 'var(--accent)'}
          />
          {stats.lastReactionMs !== null && (
            <span className="hidden sm:contents">
              <HudDivider />
              <HudStat label="REACT" value={`${stats.lastReactionMs}ms`} color="var(--primary)" />
            </span>
          )}
          {stats.bestReactionMs !== null && (
            <span className="hidden sm:contents">
              <HudStat label="BEST" value={`${stats.bestReactionMs}ms`} color="rgba(255,255,255,0.3)" />
            </span>
          )}
          <HudDivider />

          {/* Start/Stop button */}
          <button
            onClick={handleStartStop}
            className="pointer-events-auto flex items-center gap-1.5 px-2.5 py-1 rounded-lg transition-all duration-150"
            style={{
              background: isRunning ? 'rgba(255,70,85,0.12)' : 'rgba(0,255,148,0.12)',
              border: `1px solid ${isRunning ? 'rgba(255,70,85,0.3)' : 'rgba(0,255,148,0.3)'}`,
              color: isRunning ? '#FF4655' : '#00FF94',
              cursor: 'pointer',
              fontSize: '10px',
              letterSpacing: '0.08em',
              fontFamily: 'var(--font-sans)',
            }}
          >
            {isRunning ? (
              <>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <rect x="1.5" y="1" width="2.5" height="8" rx="1" fill="currentColor"/>
                  <rect x="6" y="1" width="2.5" height="8" rx="1" fill="currentColor"/>
                </svg>
                STOP
              </>
            ) : (
              <>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M2 1.5l7 3.5-7 3.5V1.5z" fill="currentColor"/>
                </svg>
                START
              </>
            )}
          </button>

          {/* Reset button */}
          <button
            onClick={handleReset}
            className="pointer-events-auto flex items-center gap-1.5 px-2.5 py-1 rounded-lg transition-all duration-150"
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.5)',
              cursor: 'pointer',
              fontSize: '10px',
              letterSpacing: '0.08em',
              fontFamily: 'var(--font-sans)',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,70,85,0.15)';
              (e.currentTarget as HTMLButtonElement).style.color = '#FF4655';
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,70,85,0.3)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.06)';
              (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.5)';
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.1)';
            }}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M1 5a4 4 0 1 0 1.2-2.8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              <path d="M1 2v3h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            RESET
          </button>

          {/* Simulation Mode indicator in HUD */}
          {simulationSettings.enabled && (
            <>
              <HudDivider />
              <div className="flex items-center gap-1" style={{ color: '#A78BFA', fontSize: '9px', letterSpacing: '0.1em', fontFamily: 'var(--font-sans)' }}>
                <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#A78BFA', boxShadow: '0 0 6px #A78BFA' }} />
                SIM
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Top Left: Core Settings ── */}
      <div className="absolute top-4 left-4 z-50 pointer-events-auto" onClick={(e) => e.stopPropagation()}>
        {/* First-visit settings hint arrow */}
        {showSettingsHint && (
          <div
            className="absolute pointer-events-none select-none"
            style={{ top: '2px', left: '44px', display: 'flex', alignItems: 'center', gap: '6px', zIndex: 60 }}
          >
            {/* Animated arrow pointing left toward the cog */}
            <svg
              width="28"
              height="18"
              viewBox="0 0 28 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                animation: 'settingsHintBounce 1.1s ease-in-out infinite',
                filter: 'drop-shadow(0 0 4px rgba(0,212,255,0.7))',
              }}
            >
              <path
                d="M26 9H4M4 9L11 3M4 9L11 15"
                stroke="#00D4FF"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span
              style={{
                color: '#00D4FF',
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '0.04em',
                textShadow: '0 0 8px rgba(0,212,255,0.6)',
                whiteSpace: 'nowrap',
                fontFamily: 'var(--font-sans)',
              }}
            >
              Settings
            </span>
          </div>
        )}
        <CoreSettingsPanel
          settings={settings}
          onChange={handleSettingsChange}
          isOpen={openPanels.has('core')}
          onOpenToggle={() => { dismissSettingsHint(); togglePanel('core'); }}
          onClose={() => togglePanel('core')}
          simulationEnabled={simulationSettings.enabled}
        />
      </div>

      {/* ── Top Right: Map menu hamburger ── */}
      <div className="absolute top-4 right-4 z-50 flex flex-col items-end gap-1.5 pointer-events-auto">
        <MapMenuPanel onMapSelect={handleMapChange} />
        {!simulationSettings.enabled && (
          <div
            className="flex items-center gap-0.5 px-1.5 py-1 rounded-xl pointer-events-auto"
            style={{
              background: 'rgba(12, 12, 18, 0.65)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid rgba(255,255,255,0.07)',
              boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
            }}
          >
            <span style={{ color: 'rgba(255,255,255,0.22)', fontSize: '8px', letterSpacing: '0.1em', fontFamily: 'var(--font-sans)', textTransform: 'uppercase', marginRight: '4px' }}>
              DIR
            </span>
            {([undefined, 'left', 'right'] as const).map((dir) => {
              const isActive = settings.spawnDirection === dir;
              const label = dir === undefined ? 'Auto' : dir === 'left' ? 'L' : 'R';
              const activeColor = dir === undefined ? '#00FF94' : '#00D4FF';
              const activeBg = dir === undefined ? 'rgba(0,255,148,0.14)' : 'rgba(0,212,255,0.14)';
              const activeBorder = dir === undefined ? 'rgba(0,255,148,0.35)' : 'rgba(0,212,255,0.35)';
              return (
                <button
                  key={String(dir)}
                  onClick={() => {
                    setSettings(prev => {
                      const next = { ...prev, spawnDirection: dir };
                      saveSettings(next);
                      return next;
                    });
                  }}
                  style={{
                    background: isActive ? activeBg : 'transparent',
                    border: `1px solid ${isActive ? activeBorder : 'transparent'}`,
                    color: isActive ? activeColor : 'rgba(255,255,255,0.35)',
                    fontSize: '9px',
                    fontFamily: 'var(--font-sans)',
                    letterSpacing: '0.06em',
                    padding: '2px 7px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    fontWeight: isActive ? 700 : 400,
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Bottom Right: Advanced Settings ── */}
      <div className="absolute bottom-4 right-4 z-50 pointer-events-auto" onClick={(e) => e.stopPropagation()}>
        <AdvancedSettingsPanel
          settings={settings}
          onChange={handleSettingsChange}
          spawnOffset={currentSpawnOffset}
          onSpawnOffsetChange={handleSpawnOffsetChange}
          onSpawnOffsetReset={handleSpawnOffsetReset}
          advancedHitbox={currentAdvancedHitbox}
          onAdvancedHitboxChange={handleAdvancedHitboxChange}
          onAdvancedHitboxReset={handleAdvancedHitboxReset}
          isOpen={openPanels.has('advanced')}
          onOpenToggle={() => togglePanel('advanced')}
          onClose={() => togglePanel('advanced')}
        />
      </div>

      {/* ── Bottom Left: Simulation Mode ── */}
      <div className="absolute bottom-4 left-4 z-50 pointer-events-auto" onClick={(e) => e.stopPropagation()}>
        <SimulationPanel
          settings={simulationSettings}
          onChange={handleSimulationSettingsChange}
          isOpen={openPanels.has('simulation')}
          onOpenToggle={() => togglePanel('simulation')}
          onClose={() => togglePanel('simulation')}
        />
      </div>

      {/* Result overlay */}
      <ResultOverlay result={roundResult} reactionMs={stats.lastReactionMs} />

    </div>
  );
}

function HudStat({ label, value, color, large, glow }: {
  label: string; value: string; color: string; large?: boolean; glow?: boolean;
}) {
  return (
    <div className="flex flex-col items-center" style={{ minWidth: large ? '36px' : '28px' }}>
      <span style={{ color: 'rgba(255,255,255,0.28)', fontSize: '8px', letterSpacing: '0.12em', fontFamily: 'var(--font-sans)', textTransform: 'uppercase' }}>
        {label}
      </span>
      <span
        key={value}
        style={{
          color,
          fontSize: large ? '22px' : '14px',
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1,
          textShadow: glow ? `0 0 12px ${color}` : 'none',
          fontFamily: 'var(--font-sans)',
          transition: 'color 0.2s ease',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function HudDivider() {
  return <div style={{ width: '1px', height: '28px', background: 'rgba(255,255,255,0.08)' }} />;
}